/**
 * StatTile — small/medium/large stat display.
 *
 * Replaces:
 *   - <IndustrySummaryTile> (the canonical, was only used by /industry)
 *   - 3+ inline copies in EmpireAggregate, AssetOverview, PersonalStats
 *
 * Sizes:
 *   - sm: text-lg value, used in dense grids (4-6 per row)
 *   - md: text-2xl value (the design-system .stat default)
 *   - lg: text-4xl value, used as page hero (e.g. "Profit potential")
 *
 * Tones (color):
 *   - default: text-eve-text
 *   - accent:  text-eve-accent (output value)
 *   - positive: text-eve-success
 *   - negative: text-eve-danger
 *   - muted:   text-eve-muted (for "sin precio" / neutral stats)
 */

export type StatSize = 'sm' | 'md' | 'lg'
export type StatTone = 'default' | 'accent' | 'positive' | 'negative' | 'muted'

const SIZE_VALUE: Record<StatSize, string> = {
  sm: 'text-lg font-mono font-semibold',
  md: 'stat', // the .stat CSS utility — 2xl mono semibold
  lg: 'font-display text-4xl font-bold',
}

const SIZE_SUB: Record<StatSize, string> = {
  sm: 'text-[11px] text-eve-muted mt-0.5 italic',
  md: 'text-xs text-eve-muted mt-1 italic',
  lg: 'text-sm text-eve-muted mt-2',
}

const TONE_VALUE: Record<StatTone, string> = {
  default: 'text-eve-text',
  accent: 'text-eve-accent',
  positive: 'text-eve-success',
  negative: 'text-eve-danger',
  muted: 'text-eve-muted',
}

export function StatTile({
  label,
  value,
  sub,
  size = 'md',
  tone = 'default',
  variant = 'default',
  className = '',
}: {
  label: string
  value: string
  sub?: string
  size?: StatSize
  tone?: StatTone
  /** `default` (the .card) | `muted` (bg-eve-bg, no accent line) */
  variant?: 'default' | 'muted'
  className?: string
}) {
  const wrapper = variant === 'muted'
    ? 'bg-eve-bg border border-eve-border rounded-md p-3'
    : 'card'
  return (
    <div className={`${wrapper} ${className}`.trim()}>
      <div className="stat-label">{label}</div>
      <div className={`${SIZE_VALUE[size]} mt-1 ${TONE_VALUE[tone]}`}>{value}</div>
      {sub && <div className={SIZE_SUB[size]}>{sub}</div>}
    </div>
  )
}
